import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduAdjunctAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { GraduationCap, Plus, Pencil, Trash2, RefreshCw, Loader2, Users, Clock, DollarSign } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const STATUSES = ['active', 'completed', 'cancelled'];
const STATUS_LABEL = { active: 'Active', completed: 'Completed', cancelled: 'Cancelled' };
const STATUS_VARIANT = { active: 'default', completed: 'secondary', cancelled: 'outline' };
const RENEWAL_LABEL = { none: 'None', requested: 'Requested', approved: 'Approved', declined: 'Declined' };

const EMPTY_FORM = {
  instructor_name: '', course_title: '', department: '', hourly_rate: 0,
  contracted_hours: 0, status: 'active',
};

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const EducationAdjunctManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const res = await eduAdjunctAPI.list();
      setAppointments(toArray(res));
    } catch (err) {
      // 402 → add-on not active; surface a clear, non-alarming message.
      if (err?.response?.status === 402) {
        setAppointments([]);
      } else {
        Swal.fire('Could not load appointments', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAppointments(); }, []);

  const stats = useMemo(() => {
    const active = appointments.filter((a) => a.status === 'active').length;
    const hours = appointments.reduce((s, a) => s + (Number(a.contracted_hours) || 0), 0);
    const cost = appointments.reduce(
      (s, a) => s + (Number(a.hourly_rate) || 0) * (Number(a.contracted_hours) || 0),
      0,
    );
    return { total: appointments.length, active, hours, cost };
  }, [appointments]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({ ...EMPTY_FORM, ...row });
    setDialogOpen(true);
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!form.instructor_name?.trim()) {
      return Swal.fire('Instructor required', 'Enter the instructor name.', 'warning');
    }
    const payload = {
      ...form,
      instructor_name: form.instructor_name.trim(),
      hourly_rate: Number(form.hourly_rate) || 0,
      contracted_hours: Number(form.contracted_hours) || 0,
    };
    try {
      if (editingId) {
        await eduAdjunctAPI.update(editingId, payload);
      } else {
        await eduAdjunctAPI.create(payload);
      }
      setDialogOpen(false);
      await loadAppointments();
      Swal.fire({ icon: 'success', title: editingId ? 'Appointment updated' : 'Appointment added', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review the form and try again.', 'error');
    }
  };

  const renew = async (row) => {
    const ok = await Swal.fire({
      icon: 'question', title: 'Renew appointment?',
      text: `${row.instructor_name || `#${row.id}`} will be renewed and set to active.`,
      showCancelButton: true, confirmButtonText: 'Renew',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduAdjunctAPI.renew(row.id);
      await loadAppointments();
      Swal.fire({ icon: 'success', title: 'Renewed', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Renew failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const remove = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete appointment?',
      text: `${row.instructor_name || `#${row.id}`}'s appointment will be removed.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduAdjunctAPI.remove(row.id);
      await loadAppointments();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-indigo-600" /> Adjunct Faculty
          </h2>
          <p className="text-sm text-muted-foreground">
            Per-term, per-course adjunct instructor appointments — hourly rates, contracted hours and renewals.
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Add Appointment
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Appointments" value={stats.total} icon={Users} />
        <StatCard label="Active" value={stats.active} icon={GraduationCap} />
        <StatCard label="Contracted Hours" value={`${stats.hours} hrs`} icon={Clock} />
        <StatCard label="Estimated Cost" value={money(stats.cost)} icon={DollarSign} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Adjunct Appointments</CardTitle>
          <CardDescription>All adjunct instructor appointments in this institute.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading appointments…
            </div>
          ) : appointments.length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="No adjunct appointments yet"
              description={canManage ? 'Add an adjunct appointment to get started.' : 'No appointments have been added yet.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Instructor</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Hours</TableHead>
                    <TableHead>Est. Cost</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Renewal</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {appointments.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.instructor_name || `#${row.faculty_profile_id || row.id}`}</TableCell>
                      <TableCell>{row.course_title || '—'}</TableCell>
                      <TableCell>{row.department || '—'}</TableCell>
                      <TableCell>{money(row.hourly_rate)}</TableCell>
                      <TableCell>{row.contracted_hours ? `${row.contracted_hours} hrs` : '—'}</TableCell>
                      <TableCell>{money(row.estimated_cost ?? (Number(row.hourly_rate) || 0) * (Number(row.contracted_hours) || 0))}</TableCell>
                      <TableCell><Badge variant={STATUS_VARIANT[row.status] || 'secondary'}>{STATUS_LABEL[row.status] || row.status}</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{RENEWAL_LABEL[row.renewal_state] || row.renewal_state}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {canManage && <Button variant="ghost" size="icon" onClick={() => renew(row)} title="Renew"><RefreshCw className="h-4 w-4 text-emerald-600" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => remove(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Appointment' : 'Add Appointment'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Instructor Name *</Label>
                <Input value={form.instructor_name || ''} onChange={(e) => setField('instructor_name', e.target.value)} placeholder="Dr. Jane Doe" />
              </div>
              <div className="space-y-1">
                <Label>Course Title</Label>
                <Input value={form.course_title || ''} onChange={(e) => setField('course_title', e.target.value)} placeholder="Intro to Statistics" />
              </div>
              <div className="space-y-1">
                <Label>Department</Label>
                <Input value={form.department || ''} onChange={(e) => setField('department', e.target.value)} placeholder="Mathematics" />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setField('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Hourly Rate</Label>
                <Input type="number" value={form.hourly_rate} onChange={(e) => setField('hourly_rate', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Contracted Hours</Label>
                <Input type="number" value={form.contracted_hours} onChange={(e) => setField('contracted_hours', e.target.value)} />
              </div>
            </DialogFormGrid>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit}>{editingId ? 'Save changes' : 'Add appointment'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationAdjunctManagement;
